/**
 * @file Pure validator for the desktop sign-in handshake parameters carried on
 * `/desktop-auth?port=<p>&state=<s>`. Consumed by `DesktopAuthPage` and by
 * `desktopAuthRequestStore`, which revalidates on every read so the
 * sessionStorage copy is never load-bearing for safety.
 *
 * Invariant: nothing downstream may treat `port` or `state` as trusted unless
 * it came out of this function. The returned `port` is the parsed integer, and
 * `state` is echoed back to the desktop agent verbatim.
 */

export type DesktopAuthRequest = { port: number; state: string };

const statePattern = /^[A-Za-z0-9_-]{43,128}$/;

/**
 * Validates the raw query parameters of a desktop sign-in request.
 *
 * `state` must be base64url (`A-Za-z0-9_-`), 43 to 128 characters: 43 is the
 * encoded length of the agent's 256-bit nonce, and the 128 ceiling lets a
 * future agent lengthen the nonce without a web deploy.
 *
 * `port` must be a canonical decimal integer in the ephemeral/unprivileged
 * range. Requiring `String(port) === rawPort` rejects `049152`, `1e4`, `0x…`,
 * whitespace-padded and fractional spellings, all of which `Number()` would
 * otherwise accept and silently canonicalise into a port the agent never
 * asked for.
 *
 * @returns The validated request, or `undefined` — the caller renders an
 * in-page error and never redirects, because an unvalidated port must not
 * receive so much as an error.
 */
export const parseDesktopAuthRequest = (
  rawPort: string | null,
  rawState: string | null,
): DesktopAuthRequest | undefined => {
  if (rawPort === null || rawState === null) return undefined;
  if (!statePattern.test(rawState)) return undefined;
  const port = Number(rawPort);
  if (!Number.isInteger(port) || String(port) !== rawPort) return undefined;
  if (port < 1024 || port > 65535) return undefined;
  return { port, state: rawState };
};
